import { APICallError } from "ai";
import type { AiModelAccess } from "@/lib/ai/model-access";
import {
  AVAILABLE_MODELS,
  MODEL_CONTEXT_LIMITS,
  type ModelId,
} from "@/lib/ai/models";

/**
 * The error categories used to choose a recovery UI without reporting expected
 * client-side failures as provider errors.
 */
type ChatExternalErrorKind =
  | "context-limit"
  | "overloaded"
  | "network"
  | "refusal"
  | "generic";

/** The raw provider or transport message paired with its recovery category. */
export type ChatExternalErrorState = {
  kind: ChatExternalErrorKind;
  rawMessage: string;
};

export const EMPTY_MODEL_REFUSAL = "Model declined to provide a response.";

const NETWORK_ERROR_MESSAGES = [
  "failed to fetch",
  "networkerror when attempting to fetch resource",
  "load failed",
  "network request failed",
  "the internet connection appears to be offline",
  "connection closed",
] as const;

type ProviderErrorBody = {
  error?: {
    type?: unknown;
    code?: unknown;
    message?: unknown;
  };
  code?: unknown;
  message?: unknown;
};

function parseProviderErrorBody(responseBody: string | undefined) {
  if (!responseBody) {
    return null;
  }

  try {
    return JSON.parse(responseBody) as ProviderErrorBody;
  } catch {
    return null;
  }
}

function normalizeText(value: unknown) {
  return typeof value === "string" ? value.toLowerCase() : "";
}

/**
 * Detects browser fetch failures that happen before an HTTP response exists.
 *
 * These failures are expected during offline transitions, proxy disconnects, or
 * tab exits, so callers can show a retry path instead of reporting them as
 * unexpected application errors.
 */
export function isNetworkTransportError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const name = normalizeText(
    "name" in error ? (error as { name?: unknown }).name : undefined,
  );
  const message = normalizeText(
    "message" in error ? (error as { message?: unknown }).message : undefined,
  );

  return (
    name === "typeerror" &&
    NETWORK_ERROR_MESSAGES.some((pattern) => message.includes(pattern))
  );
}

function isContextLengthExceededError(error: APICallError) {
  if (error.isRetryable !== false || error.statusCode !== 400) {
    return false;
  }

  const body = parseProviderErrorBody(error.responseBody);
  const errorType = normalizeText(body?.error?.type);
  const errorCode = normalizeText(body?.error?.code ?? body?.code);
  const errorMessage = normalizeText(body?.error?.message ?? body?.message);
  const fallbackMessage = normalizeText(error.message);

  if (errorCode === "context_length_exceeded") {
    return true;
  }

  if (
    errorType === "invalid_request_error" &&
    (errorMessage.includes("too long") ||
      errorMessage.includes("maximum context length") ||
      errorMessage.includes("prompt is too long"))
  ) {
    return true;
  }

  if (
    errorMessage.includes("request payload size") ||
    errorMessage.includes("context length") ||
    errorMessage.includes("maximum number of tokens") ||
    errorMessage.includes("token count exceeds")
  ) {
    return true;
  }

  return (
    fallbackMessage.includes("context length") ||
    fallbackMessage.includes("maximum number of tokens") ||
    fallbackMessage.includes("token count exceeds") ||
    fallbackMessage.includes("prompt is too long")
  );
}

function isOverloadedError(error: APICallError) {
  if (error.statusCode === 529 || error.statusCode === 503) {
    return true;
  }

  return error.isRetryable === true && error.statusCode === 529;
}

function toErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

function getAccessibleModels(
  aiModelAccess: Pick<AiModelAccess, "restrictedModelIds">,
) {
  return AVAILABLE_MODELS.filter(
    (model) => !aiModelAccess.restrictedModelIds.includes(model.value),
  );
}

/**
 * Maps an AI chat failure to the recovery state shown by the thread UI.
 *
 * Network transport failures are classified before provider errors because no
 * HTTP response exists to identify them, and they should remain retryable
 * without triggering Bugsnag notifications.
 */
export function resolveChatExternalError(
  error: unknown,
): ChatExternalErrorState {
  const rawMessage = toErrorMessage(error);

  if (rawMessage === EMPTY_MODEL_REFUSAL) {
    return { kind: "refusal", rawMessage };
  }

  if (isNetworkTransportError(error)) {
    return { kind: "network", rawMessage };
  }

  if (!APICallError.isInstance(error)) {
    return { kind: "generic", rawMessage };
  }

  if (isContextLengthExceededError(error)) {
    return { kind: "context-limit", rawMessage };
  }

  if (isOverloadedError(error)) {
    return { kind: "overloaded", rawMessage };
  }

  return { kind: "generic", rawMessage };
}

/**
 * Chooses an alternate model only for failures that model changes can resolve.
 *
 * Network failures are independent of the selected model, so callers receive
 * `null` and can keep the draft available for a retry instead.
 */
export function resolveRecommendedModelForChatExternalError(params: {
  error: ChatExternalErrorState;
  currentModel: ModelId;
  aiModelAccess: Pick<AiModelAccess, "restrictedModelIds">;
}) {
  const { error, currentModel, aiModelAccess } = params;
  const accessibleModels = getAccessibleModels(aiModelAccess).filter(
    (model) => model.value !== currentModel,
  );

  if (accessibleModels.length === 0) {
    return null;
  }

  if (error.kind === "network" || error.kind === "refusal") {
    return null;
  }

  if (error.kind === "context-limit") {
    const currentLimit = MODEL_CONTEXT_LIMITS[currentModel] ?? -1;
    const betterContextModel = accessibleModels
      .map((model) => ({
        ...model,
        contextLimit: MODEL_CONTEXT_LIMITS[model.value] ?? -1,
      }))
      .sort((left, right) => right.contextLimit - left.contextLimit)
      .find((model) => model.contextLimit > currentLimit);

    return betterContextModel ?? null;
  }

  return accessibleModels[0] ?? null;
}
